import type { Point } from "geojson";
import maplibregl, {
  type ExpressionSpecification,
  type GeoJSONSource,
  type LngLatBoundsLike,
  type MapGeoJSONFeature,
} from "maplibre-gl";

import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LayerCollection, LayerFeature, OsmCollection } from "../lib/api";
import {
  type BasemapId,
  basemapStyle,
  satelliteSource,
  US_CENTER,
  US_ZOOM,
} from "../lib/map-styles";
import { FRAME_LABEL, TIER_MAP_COLOR } from "../lib/tiers";

export type ColorBy = "tier" | "frame" | "segment" | "cooling";

export const FRAME_MAP_COLOR: Record<string, string> = {
  aero: "#2f7f7a",
  heavy_E: "#b8741a",
  heavy_F: "#6e5aa8",
  heavy_H: "#8a4a6b",
  small_industrial: "#4f7f3e",
  recip: "#3e6b93",
  unknown: "#7c7f76",
};
export const SEGMENT_MAP_COLOR: Record<string, string> = {
  A: "#b8741a",
  B: "#3e6b93",
  D: "#6e5aa8",
};
export const COOLING_MAP_COLOR: Record<string, string> = {
  none: "#4f7f3e",
  evap: "#2f7f7a",
  fog: "#b8741a",
  wet: "#b8741a",
  chiller: "#8a4a6b",
  mixed: "#6e5aa8",
  unknown: "#7c7f76",
};

export function colorExpression(colorBy: ColorBy): ExpressionSpecification {
  const table =
    colorBy === "frame"
      ? ["fc", FRAME_MAP_COLOR]
      : colorBy === "segment"
        ? ["seg", SEGMENT_MAP_COLOR]
        : colorBy === "cooling"
          ? ["ic", COOLING_MAP_COLOR]
          : ["tr", TIER_MAP_COLOR];
  const [prop, colors] = table as [string, Record<string, string>];
  const pairs: string[] = [];
  for (const [k, v] of Object.entries(colors)) if (k !== "unknown" && k !== "x") pairs.push(k, v);
  const fallback = colors.unknown ?? colors.x ?? "#7c7f76";
  return [
    "match",
    ["coalesce", ["get", prop], "unknown"],
    ...pairs,
    fallback,
  ] as unknown as ExpressionSpecification;
}

export type Camera = { center: [number, number]; zoom: number };
export type ViewState = Camera & { bounds: [number, number, number, number] };

export type PinState = { siteId: number; lat: number; lon: number } | null;

type Props = {
  features: LayerFeature[];
  osm: OsmCollection | undefined;
  basemap: BasemapId;
  satellite: boolean;
  showOsm: boolean;
  colorBy: ColorBy;
  selectedId: number | undefined;
  initialCamera: Camera | undefined;
  flyTo: (Camera & { key: number }) | undefined;
  pin: PinState;
  onPinMove: (lat: number, lon: number) => void;
  onViewChange: (v: ViewState) => void;
  onSelect: (id: number) => void;
  onNeedOsm: () => void;
};

const SITES_SRC = "sites";
const OSM_SRC = "osm";
const SAT_SRC = "sat";
const EMPTY: LayerCollection = { type: "FeatureCollection", features: [] };

declare global {
  interface Window {
    __plenumMap?: maplibregl.Map;
  }
}

function popupHtml(p: LayerFeature["properties"]): string {
  const esc = (s: string | null | undefined) =>
    (s ?? "").replace(
      /[&<>"]/g,
      (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch] ?? ch,
    );
  const score = p.sc !== null ? `score ${p.sc.toFixed(0)} · tier ${p.tr}` : "unscored";
  return `<div style="min-width:180px">
    <div style="font-weight:600">${esc(p.n)} <span style="color:var(--muted)">${esc(p.st)}</span></div>
    <div style="color:var(--muted)">${esc(p.op ?? "Operator unknown")}</div>
    <div class="num" style="margin-top:4px">${p.qu} unit${p.qu === 1 ? "" : "s"} · ${p.qm.toFixed(1)} MW · ${FRAME_LABEL[p.fc] ?? p.fc}</div>
    <div class="num" style="color:var(--muted)">${score}${p.y ? ` · since ${p.y}` : ""}</div>
  </div>`;
}

/**
 * Why the map cannot be drawn here, or null when it can. MapLibre needs WebGL; email link
 * protection that opens pages in a remote browser (Mimecast browser isolation, seen 2026-10-01 at
 * BBM Akustik) and some locked-down machines have none, and an uncaught map error took the whole
 * page down with the router's generic error screen.
 */
export function webglUnavailable(): string | null {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    return gl ? null : "WebGL is not available in this browser";
  } catch (e) {
    return e instanceof Error ? e.message : "WebGL check failed";
  }
}

/** Shown in place of the map when it cannot be drawn: what happened and where to go instead. */
export function MapUnavailable({ reason }: { reason: string }) {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return (
    <div
      className="flex h-full w-full items-center justify-center p-6"
      style={{ background: "var(--panel)" }}
      data-testid="map-unavailable"
    >
      <div className="max-w-md text-[13px]" style={{ color: "var(--muted)" }}>
        <h2 className="mb-2 text-[16px] font-semibold" style={{ color: "var(--paper)" }}>
          The map can't be drawn in this browser
        </h2>
        <p className="mb-2">
          The map needs WebGL, which is switched off here. This usually happens when a link is
          opened through an email security service that shows pages in a remote browser (for example
          Mimecast browser isolation), or on a locked-down computer.
        </p>
        <p className="mb-3">
          Everything else works: the same sites and filters are in the table, and every site page,
          chart and export is available. For the map itself, copy{" "}
          <span className="num" style={{ color: "var(--paper)" }}>
            plenumatlas.com
          </span>{" "}
          into your browser's address bar instead of clicking the link in the email.
        </p>
        <div className="flex flex-wrap gap-2">
          <a className="btn btn-primary btn-sm" href={`/table${search}`}>
            Open the table
          </a>
          <a className="btn btn-sm" href="/explore">
            Explore
          </a>
        </div>
        <p className="mt-3 text-[11px]" style={{ color: "var(--muted-2)" }}>
          Detail: {reason}
        </p>
      </div>
    </div>
  );
}

export function MapCanvas(props: Props) {
  const {
    features,
    osm,
    basemap,
    satellite,
    showOsm,
    colorBy,
    selectedId,
    flyTo,
    pin,
    onPinMove,
    onViewChange,
    onSelect,
    onNeedOsm,
  } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const styleReady = useRef(false);
  const latest = useRef({
    features,
    osm,
    satellite,
    showOsm,
    colorBy,
    selectedId,
    onViewChange,
    onSelect,
    onNeedOsm,
    onPinMove,
  });
  latest.current = {
    features,
    osm,
    satellite,
    showOsm,
    colorBy,
    selectedId,
    onViewChange,
    onSelect,
    onNeedOsm,
    onPinMove,
  };
  const initial = useRef({ basemap, camera: props.initialCamera });
  const [unavailable, setUnavailable] = useState<string | null>(() => webglUnavailable());

  // Create the map once.
  useEffect(() => {
    if (unavailable || !containerRef.current || mapRef.current) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: basemapStyle(initial.current.basemap),
        center: initial.current.camera?.center ?? US_CENTER,
        zoom: initial.current.camera?.zoom ?? US_ZOOM,
        minZoom: 2.5,
        maxZoom: 19,
        attributionControl: { compact: true },
        fadeDuration: 0,
      });
    } catch (e) {
      // the WebGL probe passed but the context still failed (driver blocklist, lost context)
      setUnavailable(e instanceof Error ? e.message.slice(0, 200) : "the map could not start");
      return;
    }
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-right");
    mapRef.current = map;
    window.__plenumMap = map;

    const addOverlays = () => {
      const s = latest.current;
      if (!map.getSource(SAT_SRC)) {
        map.addSource(SAT_SRC, satelliteSource(import.meta.env.VITE_ARCGIS_API_KEY));
        map.addLayer({
          id: "sat",
          type: "raster",
          source: SAT_SRC,
          layout: { visibility: s.satellite ? "visible" : "none" },
          paint: { "raster-opacity": 1 },
        });
      }
      if (!map.getSource(OSM_SRC)) {
        map.addSource(OSM_SRC, { type: "geojson", data: s.osm ?? EMPTY });
        map.addLayer({
          id: "osm-fill",
          type: "fill",
          source: OSM_SRC,
          minzoom: 13,
          layout: { visibility: s.showOsm ? "visible" : "none" },
          paint: {
            "fill-color": "#c4a35a",
            "fill-opacity": ["case", ["==", ["get", "id"], s.selectedId ?? -1], 0.12, 0.04],
          },
        });
        map.addLayer({
          id: "osm-line",
          type: "line",
          source: OSM_SRC,
          minzoom: 13,
          layout: { visibility: s.showOsm ? "visible" : "none" },
          paint: {
            "line-color": ["case", ["==", ["get", "id"], s.selectedId ?? -1], "#c4a35a", "#7d9a6f"],
            "line-width": ["case", ["==", ["get", "id"], s.selectedId ?? -1], 2.5, 1.2],
            "line-dasharray": [2, 1],
          },
        });
      }
      if (!map.getSource(SITES_SRC)) {
        map.addSource(SITES_SRC, {
          type: "geojson",
          data: { type: "FeatureCollection", features: s.features },
          cluster: true,
          clusterRadius: 36,
          clusterMaxZoom: 8,
          clusterProperties: { qm: ["+", ["get", "qm"]], qu: ["+", ["get", "qu"]] },
          promoteId: "id",
        });
        map.addLayer({
          id: "clusters",
          type: "circle",
          source: SITES_SRC,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "#5b6350",
            "circle-opacity": 0.85,
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["get", "point_count"],
              2,
              12,
              20,
              18,
              100,
              26,
            ],
            "circle-stroke-color": "#c4a35a",
            "circle-stroke-width": 1.5,
          },
        });
        map.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: SITES_SRC,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["Noto Sans Bold"],
            "text-size": 11,
            "text-allow-overlap": true,
          },
          paint: { "text-color": "#f4f1e6" },
        });
        map.addLayer({
          id: "site-selected",
          type: "circle",
          source: SITES_SRC,
          filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "id"], s.selectedId ?? -1]],
          paint: {
            "circle-radius": [
              "+",
              ["interpolate", ["linear"], ["sqrt", ["coalesce", ["get", "qm"], 40]], 6, 4, 32, 13],
              7,
            ],
            "circle-color": "#c4a35a",
            "circle-opacity": 0.35,
            "circle-stroke-color": "#c4a35a",
            "circle-stroke-width": 2,
          },
        });
        map.addLayer({
          id: "sites",
          type: "circle",
          source: SITES_SRC,
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": colorExpression(s.colorBy),
            "circle-opacity": 0.88,
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["sqrt", ["coalesce", ["get", "qm"], 40]],
              6,
              4,
              32,
              13,
            ],
            "circle-stroke-color": [
              "match",
              ["get", "cs"],
              "verified",
              "#c4a35a",
              "named",
              "#1c1a14",
              "#ffffff",
            ],
            "circle-stroke-width": ["match", ["get", "cs"], "none", 1, 2],
          },
        });
      }
      styleReady.current = true;
    };

    map.on("load", addOverlays);
    map.on("style.load", () => {
      // setStyle wipes overlays; rebuild them when the basemap changes.
      styleReady.current = false;
      if (map.isStyleLoaded()) addOverlays();
      else map.once("idle", addOverlays);
    });

    const emitView = () => {
      const b = map.getBounds();
      const c = map.getCenter();
      latest.current.onViewChange({
        center: [Number(c.lng.toFixed(4)), Number(c.lat.toFixed(4))],
        zoom: Number(map.getZoom().toFixed(2)),
        bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
      });
      if (map.getZoom() >= 11.5) latest.current.onNeedOsm();
    };
    map.on("moveend", emitView);
    map.once("load", emitView);

    map.on("mousemove", "sites", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0] as MapGeoJSONFeature | undefined;
      if (!f) return;
      const coords = (f.geometry as Point).coordinates as [number, number];
      if (!popupRef.current) {
        popupRef.current = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 10,
          maxWidth: "280px",
        });
      }
      popupRef.current
        .setLngLat(coords)
        .setHTML(popupHtml(f.properties as LayerFeature["properties"]))
        .addTo(map);
    });
    map.on("mouseleave", "sites", () => {
      map.getCanvas().style.cursor = "";
      popupRef.current?.remove();
    });
    map.on("click", "sites", (e) => {
      const f = e.features?.[0];
      const id = f?.properties?.id;
      if (typeof id === "number") latest.current.onSelect(id);
      else if (typeof id === "string") latest.current.onSelect(Number(id));
    });
    map.on("mouseenter", "clusters", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "clusters", () => {
      map.getCanvas().style.cursor = "";
    });
    map.on("click", "clusters", async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const clusterId = f.properties?.cluster_id as number;
      const src = map.getSource(SITES_SRC) as GeoJSONSource;
      const zoom = await src.getClusterExpansionZoom(clusterId);
      map.easeTo({
        center: (f.geometry as Point).coordinates as [number, number],
        zoom: Math.min(zoom + 0.5, 14),
      });
    });

    return () => {
      popupRef.current?.remove();
      markerRef.current?.remove();
      map.remove();
      mapRef.current = null;
      window.__plenumMap = undefined;
    };
  }, [unavailable]);

  // Basemap switch.
  const basemapRef = useRef(basemap);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || basemapRef.current === basemap) return;
    basemapRef.current = basemap;
    map.setStyle(basemapStyle(basemap), { diff: false });
  }, [basemap]);

  const whenReady = useCallback((fn: (map: maplibregl.Map) => void) => {
    const map = mapRef.current;
    if (!map) return;
    if (styleReady.current && map.getSource(SITES_SRC)) fn(map);
    else map.once("idle", () => mapRef.current && fn(mapRef.current));
  }, []);

  useEffect(() => {
    whenReady((map) => {
      (map.getSource(SITES_SRC) as GeoJSONSource | undefined)?.setData({
        type: "FeatureCollection",
        features,
      });
    });
  }, [features, whenReady]);

  useEffect(() => {
    whenReady((map) => {
      if (osm) (map.getSource(OSM_SRC) as GeoJSONSource | undefined)?.setData(osm);
    });
  }, [osm, whenReady]);

  useEffect(() => {
    whenReady((map) => {
      if (map.getLayer("sat"))
        map.setLayoutProperty("sat", "visibility", satellite ? "visible" : "none");
    });
  }, [satellite, whenReady]);

  useEffect(() => {
    whenReady((map) => {
      for (const id of ["osm-fill", "osm-line"]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", showOsm ? "visible" : "none");
      }
    });
  }, [showOsm, whenReady]);

  useEffect(() => {
    whenReady((map) => {
      if (map.getLayer("sites"))
        map.setPaintProperty("sites", "circle-color", colorExpression(colorBy));
    });
  }, [colorBy, whenReady]);

  useEffect(() => {
    whenReady((map) => {
      const sel = selectedId ?? -1;
      if (map.getLayer("site-selected")) {
        map.setFilter("site-selected", [
          "all",
          ["!", ["has", "point_count"]],
          ["==", ["get", "id"], sel],
        ]);
      }
      if (map.getLayer("osm-line")) {
        map.setPaintProperty("osm-line", "line-color", [
          "case",
          ["==", ["get", "id"], sel],
          "#c4a35a",
          "#7d9a6f",
        ]);
        map.setPaintProperty("osm-line", "line-width", [
          "case",
          ["==", ["get", "id"], sel],
          2.5,
          1.2,
        ]);
        map.setPaintProperty("osm-fill", "fill-opacity", [
          "case",
          ["==", ["get", "id"], sel],
          0.12,
          0.04,
        ]);
      }
    });
  }, [selectedId, whenReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTo) return;
    map.flyTo({ center: flyTo.center, zoom: flyTo.zoom, duration: 900, essential: true });
  }, [flyTo]);

  // Draggable verification pin (PRD §12.3 "Verify on satellite").
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pin) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }
    if (!markerRef.current) {
      const el = document.createElement("div");
      el.style.width = "18px";
      el.style.height = "18px";
      el.style.borderRadius = "50%";
      el.style.background = "#c4a35a";
      el.style.border = "3px solid #1c1a14";
      el.style.boxShadow = "0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,.5)";
      el.style.cursor = "grab";
      el.setAttribute("data-testid", "verify-pin");
      const m = new maplibregl.Marker({ element: el, draggable: true })
        .setLngLat([pin.lon, pin.lat])
        .addTo(map);
      m.on("dragend", () => {
        const ll = m.getLngLat();
        latest.current.onPinMove(Number(ll.lat.toFixed(6)), Number(ll.lng.toFixed(6)));
      });
      markerRef.current = m;
      map.flyTo({ center: [pin.lon, pin.lat], zoom: Math.max(map.getZoom(), 16), duration: 900 });
    } else {
      const cur = markerRef.current.getLngLat();
      if (Math.abs(cur.lat - pin.lat) > 1e-7 || Math.abs(cur.lng - pin.lon) > 1e-7) {
        markerRef.current.setLngLat([pin.lon, pin.lat]);
      }
    }
  }, [pin]);

  if (unavailable) return <MapUnavailable reason={unavailable} />;
  return <div ref={containerRef} className="h-full w-full" data-testid="map-canvas" />;
}

export function fitBounds(map: maplibregl.Map, bounds: LngLatBoundsLike) {
  map.fitBounds(bounds, { padding: 40, duration: 600 });
}
